import React, { useReducer } from 'react';
import { apm } from '@elastic/apm-rum';
import {
    DISABLE_NEXT,
    INCREMENT_SEARCH_COUNT,
    MEMBER_DETAILS,
    NO_MEMBER_FOUND,
    SET_ALL_PROVIDERS_POOL,
    SET_CPTT_CORRELATION_ID,
    SET_ESTIMATE_RESPONSE,
    SET_INTEGRATION_TYPE,
    SET_RX_TOGGLE,
    SET_PLAN_BENEFITS,
    SET_SINGLE_ESTIMATE_RESPONSE,
    SET_SOURCE_SYSTEM,
    UPDATE_FORM_VALUE_CUSTOM,
    UPDATE_SHOW_ERROR_PAGE_VALUE,
    SET_PREVENTIVE_ESTIMATE,
    SET_DIAGNOSTIC_ESTIMATE,
    TOGGLE_IS_PREVENTIVE,
    SET_ESTIMATE_PARAMETERS,
    CLEAR_PREVENTIVE_FLAGS,
    CLEAR_ESTIMATE_HISTORY,
    SET_RENTAL_ESTIMATES,
    SET_PURCHASE_ESTIMATES,
    SET_ESTIMATE_RESULTS_PAGE,
    SET_VISION_ESTIMATE_RESPONSE,
} from './actionTypes';
import { FormContext } from './Form.context';
import { initialFormValues } from './InitialFormValues';
import { reducer } from './Reducer';
const lodash = require('lodash');
import { ESTIMATE_HISTORY_TYPES } from '../constants/index';

/*
 * RegExp for field validation
 */
const isText = RegExp(/^[a-zA-Z\s'-]+$/i);
const isNumber = RegExp(/^\d+$/);
const isAlphanumeric = RegExp(/^[A-Z0-9 ]+$/i);
const isSpecialChar = RegExp(/[`!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/);

/*
 * Provider for Form Context
 *
 * When adding values to the Context, be sure to update:
 * 1. Form.context.js
 * 2. Form.provider.js - to implement the function with the reducer and to add the state/value to the "return" section
 * 3. Reducer.js - if any state management is required
 */
export function FormProvider({ children }) {
    var estimatesHistoryObj = {
        [ESTIMATE_HISTORY_TYPES.preventive]: {
            singleEstimate: null,
            multipleEstimate: null,
        },
        [ESTIMATE_HISTORY_TYPES.diagnostic]: {
            singleEstimate: null,
            multipleEstimate: null,
        },
        [ESTIMATE_HISTORY_TYPES.purchase]: {
            singleEstimate: null,
            multipleEstimate: null,
        },
        [ESTIMATE_HISTORY_TYPES.rental]: {
            singleEstimate: null,
            multipleEstimate: null,
        },
    };

    const [
        {
            allProvidersPool,
            singleEstimateResponse,
            visionEstimateResponse,
            estimateResponse,
            formValues,
            memberDetails,
            planBenefits,
            noMemberFound,
            disableNext,
            integrationType,
            rxToggle,
            sourceSystem,
            showErrorPage,
            cpttCorrelationId,
            searchCount,
            isPreventive,
            estimateParameters,
            estimatesHistory,
            estimateResultsPage,
        },
        dispatch,
    ] = useReducer(reducer, {
        showErrorPage: false,
        formValues: { ...initialFormValues },
        memberDetails: {},
        noMemberFound: false,
        disableNext: false,
        planBenefits: {},
        integrationType: 'member',
        rxToggle: true,
        sourceSystem: 'epe',
        searchCount: 0,
        isPreventive: false,
        estimateParameters: {},
        estimatesHistory: {
            ...estimatesHistoryObj,
        },
        estimateResultsPage: 0,
    });

    apm.setCustomContext({
        correlationId: `${cpttCorrelationId}`,
        sourceSystem: `${sourceSystem}`,
    });

    const setEstimateResultsPage = (page) => {
        dispatch({
            type: SET_ESTIMATE_RESULTS_PAGE,
            value: page,
        });
    };

    const setEstimateHistory = (
        multipleEstimateResponse,
        singleEstimateResponse,
        nextIsPreventiveState
    ) => {
        if (formValues.selectedService.value.fullCarePath.preventive) {
            if (nextIsPreventiveState) {
                setPreventiveEstimateHistory(multipleEstimateResponse, singleEstimateResponse);
            } else {
                setDiagnosticEstimateHistory(multipleEstimateResponse, singleEstimateResponse);
            }
        } else {
            const modifierValue =
                multipleEstimateResponse?.scenarios[0]?.claims[0]?.procedures[0]?.codeModifier ??
                singleEstimateResponse?.claims[0]?.procedures[0]?.codeModifier;
            if (modifierValue == ESTIMATE_HISTORY_TYPES.rental) {
                setDmeRentalEstimates(multipleEstimateResponse, singleEstimateResponse);
            } else {
                setDmePurchaseEstimates(multipleEstimateResponse, singleEstimateResponse);
            }
        }
    };

    const setPreventiveEstimateHistory = (multipleEstimateResponse, singleEstimateResponse) => {
        dispatch({
            type: SET_PREVENTIVE_ESTIMATE,
            multipleEstimateResponse: multipleEstimateResponse,
            singleEstimateResponse: singleEstimateResponse,
        });
    };

    const setDiagnosticEstimateHistory = (multipleEstimateResponse, singleEstimateResponse) => {
        dispatch({
            type: SET_DIAGNOSTIC_ESTIMATE,
            multipleEstimateResponse: multipleEstimateResponse,
            singleEstimateResponse: singleEstimateResponse,
        });
    };

    const clearEstimateHistory = () => {
        dispatch({ type: CLEAR_ESTIMATE_HISTORY });
    };

    const setIsPreventive = () => {
        return dispatch({ type: TOGGLE_IS_PREVENTIVE });
    };

    const clearPreventiveFlags = () => {
        dispatch({ type: CLEAR_PREVENTIVE_FLAGS });
    };

    const haveMemberDetails = () => {
        return !lodash.isEmpty(memberDetails);
    };

    const resetMemberDetails = () => {
        dispatch({ type: MEMBER_DETAILS, body: {} });
    };

    // Reset to first step, clear to initial values
    const handleReset = () => {
        dispatch({ type: 'reset' });
    };

    /**
     * convenience function to dispatch a change to cpttCorrelationID easily
     * @param {String} correlationid - the correlation ID to be set
     */
    const setCpttCorrelationId = (correlationid) => {
        return dispatch({
            type: SET_CPTT_CORRELATION_ID,
            value: correlationid,
        });
    };

    const updateIsErrorPageVisible = (val) => {
        return dispatch({ type: UPDATE_SHOW_ERROR_PAGE_VALUE, payload: val });
    };

    const setAllProvidersPool = (allProvidersPool) => {
        return dispatch({
            type: SET_ALL_PROVIDERS_POOL,
            allProvidersPool,
        });
    };

    const setEstimateResponse = (estimateResponse, nextIsPreventiveState = null) => {
        nextIsPreventiveState = nextIsPreventiveState ?? isPreventive;
        setEstimateHistory(estimateResponse, null, nextIsPreventiveState);
        return dispatch({
            type: SET_ESTIMATE_RESPONSE,
            estimateResponse,
        });
    };

    const setSingleEstimateResponse = (singleEstimateResponse, nextIsPreventiveState = null) => {
        nextIsPreventiveState = nextIsPreventiveState ?? isPreventive;
        setEstimateHistory(null, singleEstimateResponse, nextIsPreventiveState);
        return dispatch({
            type: SET_SINGLE_ESTIMATE_RESPONSE,
            singleEstimateResponse,
        });
    };

    const setVisionEstimateResponse = (visionEstimateResponse) => {
        return dispatch({
            type: SET_VISION_ESTIMATE_RESPONSE,
            visionEstimateResponse,
        });
    };

    const setDmeRentalEstimates = (multipleEstimateResponse, singleEstimateResponse) => {
        dispatch({
            type: SET_RENTAL_ESTIMATES,
            multipleEstimateResponse: multipleEstimateResponse,
            singleEstimateResponse: singleEstimateResponse,
        });
    };

    const setDmePurchaseEstimates = (multipleEstimateResponse, singleEstimateResponse) => {
        dispatch({
            type: SET_PURCHASE_ESTIMATES,
            multipleEstimateResponse: multipleEstimateResponse,
            singleEstimateResponse: singleEstimateResponse,
        });
    };

    const setEstimateParameters = (parameters) => {
        dispatch({
            type: SET_ESTIMATE_PARAMETERS,
            payload: parameters,
        });
    };

    const validateInputField = (fieldType, fieldValue, helperText, currentError) => {
        let error = currentError;
        switch (fieldType) {
            case 'text':
                if (fieldValue && !isText.test(fieldValue))
                    error = helperText || 'This field accepts text only.';
                break;

            case 'number':
                if (fieldValue && !isNumber.test(fieldValue))
                    error = helperText || 'This field accepts numbers only.';
                break;

            case 'alphanumeric':
                if (fieldValue && !isAlphanumeric.test(fieldValue))
                    error = helperText || 'This field accepts alphanumerics only.';
                break;
            case 'alphanumericOrSpecialChar':
                if (
                    fieldValue &&
                    !(isAlphanumeric.test(fieldValue) || isSpecialChar.test(fieldValue))
                ) {
                    error = helperText || 'This value contains an invalid character.';
                }
                break;

            default:
                break;
        }
        return error;
    };

    // Handle form change
    const handleChange = (event, checked) => {
        const { type, name, value } = event.target;
        let fieldValue = value;
        if (type === 'checkbox') {
            fieldValue = checked ? 'on' : '';
        }
        dispatch({ type: 'form-value', name, fieldValue });

        if (name == 'groupNumber') {
            // Truncating first 14 characters since that is the limit of CDB which is what OBAPI gets there memberInformation from
            let truncValue = fieldValue.trim().slice(0, 14);
            dispatch({
                type: UPDATE_FORM_VALUE_CUSTOM,
                name,
                fieldValue: truncValue,
                keyName: 'truncated',
            });
        }

        const fieldName = formValues[name];
        if (!fieldName) {
            return;
        }

        const { required, validate, helperText } = fieldName;

        let error = '';

        if (required && !fieldValue) {
            error = 'This field is required';
        }
        if (validate) {
            error = validateInputField(validate, value, helperText, error);
        }

        dispatch({ type: 'form-error', name, error });
    };

    const validateFormFieldsOnSubmit = (group = []) => {
        if (!group.length) return [];
        let firstMissingField = false; //reset the flag that checks for the first field that's missing
        group.map((key, index) => {
            const fieldName = formValues[key];
            if (!fieldName) return;
            const { required, validate, helperText, value } = fieldName;
            let error = '';
            if (required && !value) {
                error = 'This field is required';
                if (index != 2 && !firstMissingField) {
                    document.getElementsByName(key)[0].focus();
                    firstMissingField = true;
                } //focuses the missing field, except DOB, if it's the first missing field
                else if (index == 2 && !firstMissingField) {
                    document.getElementsByClassName('tds-date-picker__input')[0].focus();
                    firstMissingField = true;
                } //focuses on the missing DOB field, if it's the first missing field
            }
            if (validate) {
                error = validateInputField(validate, value, helperText, error);
            }
            dispatch({ type: 'form-error', name: key, error });
        });
    };

    const handleChangeCustom = (value, name) => {
        const fieldValue = value;

        dispatch({ type: 'form-value', name, fieldValue });

        const contextObj = formValues[name];

        if (!contextObj) return;

        const { required, validate, helperText } = contextObj;

        let error = '';

        if (required && !fieldValue) {
            error = 'This field is required';
        }
        if (validate) {
            switch (validate) {
                case 'text':
                    if (fieldValue && !isText.test(fieldValue))
                        error = helperText || 'This field accepts text only.';
                    break;

                case 'number':
                    if (fieldValue && !isNumber.test(fieldValue))
                        error = helperText || 'This field accepts numbers only.';
                    break;

                default:
                    break;
            }
        }

        dispatch({ type: 'form-error', name, error });
    };

    const setIntegrationType = (intType) => {
        return dispatch({
            type: SET_INTEGRATION_TYPE,
            intType,
        });
    };

    const setRxToggle = (rxToggle) => {
        return dispatch({
            type: SET_RX_TOGGLE,
            rxToggle,
        });
    };

    const setSourceSystem = (system) => {
        return dispatch({
            type: SET_SOURCE_SYSTEM,
            system,
        });
    };

    const disableNextButton = (disable = false) => {
        dispatch({
            type: DISABLE_NEXT,
            disable: disable,
        });
    };

    const incrementSearchCount = () => {
        dispatch({
            type: INCREMENT_SEARCH_COUNT,
        });
    };

    const getIncrementedCorId = () => {
        return `${cpttCorrelationId}-${searchCount}`;
    };

    const getCorID = () => {
        return `${cpttCorrelationId}`;
    };

    const setNoMemberFound = (notFound) => {
        dispatch({ type: NO_MEMBER_FOUND, value: notFound });
    };

    const addMemberDetailsToContext = (memberDetails) => {
        const {
            benefitsServiceResponse,
            subscriberId,
            alternateId,
            locationOfService,
            lineOfBusiness,
            cae,
            sourceCode,
            policyNumber,
            formValues,
        } = memberDetails;
        const { groupNumber, memberNumber } = formValues;
        dispatch({ type: MEMBER_DETAILS, body: benefitsServiceResponse });

        // UMR eligibility specific context changes
        setUmrFormValues(benefitsServiceResponse, groupNumber?.value, memberNumber.value);

        handleChangeCustom(subscriberId, 'subscriberId');
        handleChangeCustom(alternateId, 'alternateId');
        handleChangeCustom(locationOfService, 'locationOfService');
        handleChangeCustom(lineOfBusiness, 'lineOfBusiness');
        handleChangeCustom(cae, 'cae');
        handleChangeCustom(sourceCode, 'sourceCode');
        handleChangeCustom(policyNumber, 'policyNumber');
    };

    const setPlanBenefits = (planBenefitInfo) => {
        return dispatch({
            type: SET_PLAN_BENEFITS,
            planBenefitInfo,
        });
    };

    /**
     * UMR requires memberNumber & groupNumber to be capitalized downstream as well. Changes formValues
     * @param {object} benefitsServiceResponse - OBAPI/UMR member profile response
     * @param {string} groupNumber from formValues
     * @param {string} memberNumber from formValues
     */
    const setUmrFormValues = (benefitsServiceResponse, groupNumber, memberNumber) => {
        if (benefitsServiceResponse.sourceCode == 'UM') {
            handleChangeCustom(memberNumber.toUpperCase(), 'memberNumber');
            handleChangeCustom(groupNumber.toUpperCase(), 'groupNumber');
        }
    };

    /**
     * thanks tempo
     * @param {string} dateOfBirth string representing a dob
     * @returns {boolean} true if in range false otherwise
     */
    const isValidDate = (dateOfBirth) => {
        return (
            new Date('1900-01-01') <= new Date(dateOfBirth) && new Date(dateOfBirth) <= new Date()
        );
    };

    /**
     * removrailing and leading white space from member form data
     */
    const sanitizeMemberFormData = () => {
        const { firstName, lastName, groupNumber, memberNumber, dateOfBirth } = formValues;

        handleChangeCustom(firstName.value.trim(), 'firstName');
        handleChangeCustom(lastName.value.trim(), 'lastName');
        handleChangeCustom(groupNumber.value.trim(), 'groupNumber');
        handleChangeCustom(memberNumber.value.trim(), 'memberNumber');
        handleChangeCustom(dateOfBirth.value.trim(), 'dateOfBirth');
    };

    /**
     * Validates the form values
     * @returns {Boolean}
     */
    const validateFormData = () => {
        const { firstName, lastName, groupNumber, memberNumber, dateOfBirth, disclaimerCheck } =
            formValues;
        return (
            firstName.value.length > 0 &&
            lastName.value.length > 0 &&
            groupNumber.value.length > 0 &&
            memberNumber.value.length > 0 &&
            dateOfBirth.value.length > 0 &&
            isValidDate(dateOfBirth.value) &&
            disclaimerCheck.value
        );
    };

    return (
        <FormContext.Provider
            value={{
                memberDetails,
                haveMemberDetails,
                resetMemberDetails,
                allProvidersPool,
                setAllProvidersPool,
                singleEstimateResponse,
                setSingleEstimateResponse,
                estimateResponse,
                setEstimateResponse,
                formValues,
                validateFormFieldsOnSubmit,
                handleChange,
                handleChangeCustom,
                handleReset,
                noMemberFound,
                disableNext,
                validateFormData,
                disableNextButton,
                integrationType,
                rxToggle,
                setIntegrationType,
                setRxToggle,
                sourceSystem,
                setSourceSystem,
                showErrorPage,
                updateIsErrorPageVisible,
                incrementSearchCount,
                getIncrementedCorId,
                getCorID,
                searchCount,
                setNoMemberFound,
                addMemberDetailsToContext,
                planBenefits,
                setPlanBenefits,
                setCpttCorrelationId,
                sanitizeMemberFormData,
                isPreventive,
                setIsPreventive,
                estimateParameters,
                setEstimateParameters,
                clearPreventiveFlags,
                estimatesHistory,
                clearEstimateHistory,
                estimateResultsPage,
                setEstimateResultsPage,
                visionEstimateResponse,
                setVisionEstimateResponse,
            }}
        >
            {children}
        </FormContext.Provider>
    );
}
